import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, API_BASE_URL } from "../lib/apiClient";

export type TicketStatus = "OPEN" | "IN_PROGRESS" | "RESOLVED" | "CLOSED";
export type TicketPriority = "LOW" | "MEDIUM" | "HIGH" | "URGENT";

export interface TicketAttachment {
  id: string;
  fileName: string;
  mimeType: string;
  fileSizeBytes: number;
  uploadedBy?: { id: string; name: string };
  /** request 1231: attached with this update — null when attached to the ticket itself */
  commentId?: string | null;
  createdAt: string;
}

/** One entry in a ticket's update history: a status change and/or a note, or a reply. */
export interface TicketComment {
  id: string;
  authorId: string;
  authorName: string;
  body: string;
  statusFrom: TicketStatus | null;
  statusTo: TicketStatus | null;
  /** people @tagged in this update */
  mentionedUserIds?: string[];
  createdAt: string;
}

export interface Ticket {
  id: string;
  ticketId: string;
  title: string;
  description: string;
  status: TicketStatus;
  priority: TicketPriority;
  createdById: string;
  createdBy?: { id: string; name: string; workEmail: string };
  resolutionNote: string | null;
  resolvedAt: string | null;
  /** request 1178 */
  dueBy?: string | null;
  dueBySetBy?: string | null;
  /** request 1201: a Super Admin raised it for the person it belongs to */
  raisedByName?: string | null;
  createdAt: string;
  updatedAt: string;
  attachments?: TicketAttachment[];
  comments?: TicketComment[];
  /** names of everyone @tagged on the ticket */
  mentionedPeople?: Array<{ id: string; name: string }>;
}

export function useMyTickets() {
  return useQuery({ queryKey: ["tickets", "mine"], queryFn: async () => (await api.get<{ data: Ticket[] }>("/tickets/mine")).data.data });
}

/** Other people's tickets I was @tagged on. */
export function useTaggedTickets() {
  return useQuery({ queryKey: ["tickets", "tagged"], queryFn: async () => (await api.get<{ data: Ticket[] }>("/tickets/tagged")).data.data });
}

export function useAllTickets(status: TicketStatus | "" = "", opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["tickets", "all", status],
    queryFn: async () => (await api.get<{ data: Ticket[] }>("/tickets", { params: status ? { status } : {} })).data.data,
    enabled: opts.enabled ?? true,
  });
}

export function useTicket(ticketId: string | undefined) {
  return useQuery({
    queryKey: ["tickets", "detail", ticketId],
    queryFn: async () => (await api.get<{ data: Ticket }>(`/tickets/${ticketId}`)).data.data,
    enabled: !!ticketId,
  });
}

export function useCreateTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { title: string; description: string; priority?: TicketPriority; customerId?: string; onBehalfOfUserId?: string }) =>
      (await api.post<{ data: Ticket }>("/tickets", payload)).data.data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tickets"] }),
  });
}

export function useSetTicketDue() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ ticketId, dueBy }: { ticketId: string; dueBy: string | null }) => (await api.patch<{ data: Ticket }>(`/tickets/${ticketId}/due`, { dueBy })).data.data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tickets"] });
      qc.invalidateQueries({ queryKey: ["my-workload"] });
    },
  });
}

export function useUpdateTicketStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ ticketId, status, resolutionNote, mentionedUserIds, withFiles }: { ticketId: string; status: TicketStatus; resolutionNote?: string; mentionedUserIds?: string[]; withFiles?: boolean }) =>
      (await api.patch<{ data: Ticket & { commentId: string | null } }>(`/tickets/${ticketId}/status`, { status, resolutionNote, mentionedUserIds, withFiles })).data.data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tickets"] }),
  });
}

export function useAddTicketComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ ticketId, body, mentionedUserIds, withFiles }: { ticketId: string; body: string; mentionedUserIds?: string[]; withFiles?: boolean }) =>
      (await api.post<{ data: TicketComment }>(`/tickets/${ticketId}/comments`, { body, mentionedUserIds, withFiles })).data.data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tickets"] }),
  });
}

export function useUploadTicketAttachment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ ticketId, file, commentId }: { ticketId: string; file: File; commentId?: string | null }) => {
      const form = new FormData();
      form.append("file", file);
      if (commentId) form.append("commentId", commentId); // request 1231: attached with this update
      return api.post(`/tickets/${ticketId}/attachments`, form, { headers: { "Content-Type": "multipart/form-data" } });
    },
    onSuccess: (_res, { ticketId }) => qc.invalidateQueries({ queryKey: ["tickets", "detail", ticketId] }),
  });
}

/** Request 1299 — Super Admin only; the person who raised it isn't told. */
export function useDeleteTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ticketId: string) => api.delete(`/tickets/${ticketId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tickets"] });
      qc.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
}

export function useDeleteTicketAttachment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ ticketId, attachmentId }: { ticketId: string; attachmentId: string }) =>
      api.delete(`/tickets/${ticketId}/attachments/${attachmentId}`),
    onSuccess: (_res, { ticketId }) => qc.invalidateQueries({ queryKey: ["tickets", "detail", ticketId] }),
  });
}

export function ticketAttachmentUrl(ticketId: string, attachmentId: string): string {
  return `${API_BASE_URL}/tickets/${ticketId}/attachments/${attachmentId}/download`;
}
